<template>
    <v-container fluid class="pa-4">

        <!-- Intestazione pagina -->
        <div class="d-flex align-center mb-4">
            <div>
                <div class="text-h5 font-weight-bold">Gestione utenti</div>
                <div class="text-body-2 text-medium-emphasis">
                    Gestisci gli utenti associati alla tua azienda
                </div>
            </div>
            <v-spacer />
            <v-btn color="primary" prepend-icon="mdi-account-plus" @click="apriDialogoNuovoUtente">
                Nuovo utente
            </v-btn>
        </div>

        <!-- Card tabella -->
        <v-card rounded="lg" elevation="1">

            <!-- Toolbar con ricerca -->
            <v-toolbar flat color="transparent" class="px-2">
                <v-text-field v-model="ricerca" prepend-inner-icon="mdi-magnify" placeholder="Cerca per nome o email…"
                    variant="outlined" density="compact" hide-details clearable style="max-width: 340px;" />
                <v-spacer />
                <v-btn icon="mdi-refresh" variant="text" :loading="caricamento" @click="caricaUtenti" />
            </v-toolbar>

            <v-divider />

            <!-- Tabella -->
            <v-data-table :headers="headers" :items="utentiFiltrati" :loading="caricamento" :items-per-page="15"
                :items-per-page-options="[10, 15, 25, 50]" hover no-data-text="Nessun utente trovato"
                loading-text="Caricamento utenti..." hide-default-footer>
                <!-- Colonna nome utente -->
                <template v-slot:[`item.userName`]="{ item }">
                    <div class="d-flex align-center">
                        <v-avatar :color="item.enabled ? 'primary' : 'grey'" size="32" class="mr-3">
                            <span class="text-caption text-white font-weight-bold">
                                {{ iniziali(item.userName) }}
                            </span>
                        </v-avatar>
                        <div>
                            <div class="text-body-2 font-weight-medium">{{ item.userName }}</div>
                            <div class="text-caption text-medium-emphasis">{{ item.eMail }}</div>
                        </div>
                    </div>
                </template>

                <!-- Colonna stato -->
                <template v-slot:[`item.enabled`]="{ item }">
                    <v-chip :color="item.enabled ? 'success' : 'error'" :text="item.enabled ? 'Attivo' : 'Disabilitato'"
                        size="small" variant="tonal" />
                </template>

                <!-- Colonna email confermata -->
                <template v-slot:[`item.eMailConfirmed`]="{ item }">
                    <v-icon :icon="item.eMailConfirmed ? 'mdi-check-circle' : 'mdi-clock-outline'"
                        :color="item.eMailConfirmed ? 'success' : 'warning'" size="20" />
                </template>

                <!-- Colonna ultima attività -->
                <template v-slot:[`item.dateLastActivity`]="{ item }">
                    <span class="text-body-2 text-medium-emphasis">
                        {{ formatData(item.dateLastActivity) }}
                    </span>
                </template>

                <!-- Colonna azioni -->
                <template v-slot:[`item.azioni`]="{ item }">
                    <div class="d-flex align-center justify-end gap-1">

                        <!-- Abilita/Disabilita -->
                        <v-tooltip :text="item.enabled ? 'Disabilita utente' : 'Abilita utente'" location="top">
                            <template #activator="{ props }">
                                <v-btn v-bind="props" :icon="item.enabled ? 'mdi-account-off' : 'mdi-account-check'"
                                    :color="item.enabled ? 'warning' : 'success'" variant="text" size="small"
                                    @click="toggleAbilitazione(item)" />
                            </template>
                        </v-tooltip>

                        <!-- Gestione menu -->
                        <v-tooltip text="Gestisci menu" location="top">
                            <template #activator="{ props }">
                                <v-btn v-bind="props" icon="mdi-menu" color="info" variant="text" size="small"
                                    @click="apriDialogoMenu(item)" />
                            </template>
                        </v-tooltip>

                        <!-- Modifica -->
                        <v-tooltip text="Modifica utente" location="top">
                            <template #activator="{ props }">
                                <v-btn v-bind="props" icon="mdi-pencil" color="primary" variant="text" size="small"
                                    @click="apriDialogoModificaUtente(item)" />
                            </template>
                        </v-tooltip>

                        <!-- Reset password -->
                        <v-tooltip text="Invia reset password" location="top">
                            <template #activator="{ props }">
                                <v-btn v-bind="props" icon="mdi-lock-reset" color="secondary" variant="text"
                                    size="small" @click="inviaResetPassword(item)" />
                            </template>
                        </v-tooltip>

                        <!-- Elimina -->
                        <v-tooltip text="Elimina utente" location="top">
                            <template #activator="{ props }">
                                <v-btn v-bind="props" icon="mdi-delete" color="error" variant="text" size="small"
                                    @click="confermaElimina(item)" />
                            </template>
                        </v-tooltip>

                    </div>
                </template>

            </v-data-table>
        </v-card>

        <!-- Dialog conferma eliminazione -->
        <v-dialog v-model="dialogElimina" max-width="420">
            <v-card>
                <v-card-title class="d-flex align-center pa-4">
                    <v-icon icon="mdi-alert-circle" color="error" class="mr-2" />
                    Conferma eliminazione
                </v-card-title>
                <v-card-text>
                    Stai per eliminare l'utente
                    <strong>{{ utenteSelezionato?.userName }}</strong>.
                    L'operazione è irreversibile e rimuoverà anche le impostazioni menu associate.
                </v-card-text>
                <v-card-actions class="pa-4">
                    <v-spacer />
                    <v-btn variant="text" @click="dialogElimina = false">Annulla</v-btn>
                    <v-btn color="error" variant="elevated" :loading="eliminando" @click="eliminaUtente">
                        Elimina
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Snackbar notifiche -->
        <v-snackbar v-model="snackbar.visibile" :color="snackbar.colore" :timeout="3000" location="bottom right">
            {{ snackbar.testo }}
        </v-snackbar>

        <!-- Componenti dialog -->
        <DialogUtente ref="dialogoUtente" @salvato="onUtenteSalvato" />
        <DialogMenu ref="dialogoMenu" @salvato="notifica('Menu aggiornato', 'success')" />

    </v-container>
</template>

<script>
import {
    get_utenti,
    elimina_utente,
    imposta_abilitazione,
    invia_reset_password
} from '@/api/api.amministrazione'
import { get_mio_abbonamento } from '@/api/api.abbonamento.js'

import DialogUtente from '@/components/utenti/DialogUtente.vue';
import DialogMenu from '@/components/utenti/DialogMenu.vue'

export default {
    name: 'GestioneUtenti',

    components: {
        DialogUtente,
        DialogMenu
    },

    data() {
        return {
            utenti: [],
            caricamento: false,
            ricerca: '',

            // Elimina
            dialogElimina: false,
            eliminando: false,
            utenteSelezionato: null,

            // Snackbar
            snackbar: {
                visibile: false,
                testo: '',
                colore: 'success'
            },

            headers: [
                { title: 'Utente', key: 'userName', sortable: true },
                { title: 'Stato', key: 'enabled', sortable: true, width: '100px' },
                { title: 'Email confermata', key: 'eMailConfirmed', sortable: false, align: 'center', width: '140px' },
                { title: 'Ultima attività', key: 'dateLastActivity', sortable: true, width: '160px' },
                { title: '', key: 'azioni', sortable: false, align: 'end', width: '180px' },
            ]
        }
    },

    computed: {
        utentiFiltrati() {
            if (!this.ricerca) return this.utenti
            const q = this.ricerca.toLowerCase()
            return this.utenti.filter(u =>
                u.userName?.toLowerCase().includes(q) ||
                u.eMail?.toLowerCase().includes(q)
            )
        }
    },

    mounted() {
        this.caricaUtenti()
    },

    methods: {
        // ─── Dati ─────────────────────────────────────────────────────────────

        async caricaUtenti() {
            this.caricamento = true
            try {
                const { data } = await get_utenti()
                this.utenti = data
            } finally {
                this.caricamento = false
            }
        },

        // ─── Dialoghi ─────────────────────────────────────────────────────────

        async apriDialogoNuovoUtente() {
            // Verifica limite utenti
            try {
                const res = await get_mio_abbonamento()
                const abb = res.data
                if (abb && this.utenti.length >= abb.maxUtenti) {
                    this.notifica(
                        `Limite utenti raggiunto (${abb.maxUtenti}). ` +
                        'Richiedi utenti aggiuntivi dalla sezione Profilo → Licenza.',
                        'warning'
                    )
                    return
                }
            } catch { /* se fallisce, lascia aprire il dialog comunque */ }

            this.$refs.dialogoUtente.apri()
        },

        apriDialogoModificaUtente(utente) {
            this.$refs.dialogoUtente.apri(utente)
        },

        apriDialogoMenu(utente) {
            this.$refs.dialogoMenu.apri(utente)
        },

        // ─── Azioni ───────────────────────────────────────────────────────────

        onUtenteSalvato() {
            this.caricaUtenti()
            this.notifica('Utente salvato con successo', 'success')
        },

        async toggleAbilitazione(utente) {
            try {
                await imposta_abilitazione(utente.userID, !utente.enabled)
                utente.enabled = !utente.enabled
                this.notifica(
                    utente.enabled ? 'Utente abilitato' : 'Utente disabilitato',
                    utente.enabled ? 'success' : 'warning'
                )
            } catch {
                // gestito dall'interceptor
            }
        },

        confermaElimina(utente) {
            this.utenteSelezionato = utente
            this.dialogElimina = true
        },

        async eliminaUtente() {
            this.eliminando = true
            try {
                await elimina_utente(this.utenteSelezionato.userID)
                this.utenti = this.utenti.filter(u => u.userID !== this.utenteSelezionato.userID)
                this.dialogElimina = false
                this.notifica('Utente eliminato', 'success')
            } finally {
                this.eliminando = false
            }
        },

        async inviaResetPassword(utente) {
            try {
                await invia_reset_password(utente.userID)
                this.notifica(`Email di reset inviata a ${utente.eMail}`, 'info')
            } catch {
                // gestito dall'interceptor
            }
        },

        // ─── Utilità ──────────────────────────────────────────────────────────

        iniziali(nome) {
            if (!nome) return '?'
            return nome.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2)
        },

        formatData(data) {
            if (!data) return '—'
            return new Date(data).toLocaleString('it-IT', {
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit'
            })
        },

        notifica(testo, colore = 'success') {
            this.snackbar = { visibile: true, testo, colore }
        },
    }
}
</script>